// Next.js integration
import { Session, Scope } from '../types'

// Next.js types - these will be available when Next.js is installed
interface NextRequest {
  nextUrl: { pathname: string }
  url: string
  cookies: { get: (name: string) => { value: string } | undefined }
}

interface NextResponse {
  next(): NextResponse
  redirect(url: URL): NextResponse
  headers: { set: (name: string, value: string) => void }
}

const NextResponse = {
  next: () => ({} as NextResponse),
  redirect: (url: URL) => ({} as NextResponse)
} as any

export interface AuthMiddlewareOptions {
  publicRoutes?: string[]
  onUnauthenticated?: (req: NextRequest) => NextResponse
  onUnauthorized?: (req: NextRequest, session: Session, requiredScopes: Scope[]) => NextResponse
}

export function withAuth(options: AuthMiddlewareOptions = {}) {
  const {
    publicRoutes = [],
    onUnauthenticated,
    onUnauthorized
  } = options

  return async function middleware(req: NextRequest) {
    const { pathname } = req.nextUrl

    // Check if route is public
    if (isPublicRoute(pathname, publicRoutes)) {
      return NextResponse.next()
    }

    // Get session from cookie
    const session = await getSession(req)
    if (!session) {
      if (onUnauthenticated) {
        return onUnauthenticated(req)
      }
      return NextResponse.redirect(new URL('/signin', req.url))
    }

    // Check if session is expired
    if (session.expiresAt < new Date()) {
      if (onUnauthenticated) {
        return onUnauthenticated(req)
      }
      return NextResponse.redirect(new URL('/signin', req.url))
    }

    // Add session to headers for API routes
    const response = NextResponse.next()
    response.headers.set('x-session-id', session.id)
    response.headers.set('x-user-id', session.userId)
    if (session.orgId) {
      response.headers.set('x-org-id', session.orgId)
    }
    if (session.plan) {
      response.headers.set('x-plan', session.plan)
    }

    return response
  }
}

export function withScopes(requiredScopes: Scope[], options: AuthMiddlewareOptions = {}) {
  const {
    publicRoutes = [],
    onUnauthenticated,
    onUnauthorized
  } = options

  return async function middleware(req: NextRequest) {
    const { pathname } = req.nextUrl

    // Check if route is public
    if (isPublicRoute(pathname, publicRoutes)) {
      return NextResponse.next()
    }

    // Get session from cookie
    const session = await getSession(req)
    if (!session) {
      if (onUnauthenticated) {
        return onUnauthenticated(req)
      }
      return NextResponse.redirect(new URL('/signin', req.url))
    }

    // Check if session is expired
    if (session.expiresAt < new Date()) {
      if (onUnauthenticated) {
        return onUnauthenticated(req)
      }
      return NextResponse.redirect(new URL('/signin', req.url))
    }

    // Check if user has required scopes
    const hasRequiredScopes = requiredScopes.every(scope => 
      session.entitlements.includes(scope)
    )

    if (!hasRequiredScopes) {
      if (onUnauthorized) {
        return onUnauthorized(req, session, requiredScopes)
      }
      return new NextResponse('Forbidden', { status: 403 })
    }

    // Add session to headers for API routes
    const response = NextResponse.next()
    response.headers.set('x-session-id', session.id)
    response.headers.set('x-user-id', session.userId)
    if (session.orgId) {
      response.headers.set('x-org-id', session.orgId)
    }
    if (session.plan) {
      response.headers.set('x-plan', session.plan)
    }

    return response
  }
}

export async function getSession(req: NextRequest): Promise<Session | null> {
  const sessionCookie = req.cookies.get('kitium.session')
  if (!sessionCookie) {
    return null
  }

  try {
    // Decode and parse session cookie
    const sessionData = JSON.parse(decodeURIComponent(sessionCookie.value))
    
    // Validate session structure
    if (!sessionData.id || !sessionData.userId || !sessionData.expiresAt) {
      return null
    }

    // Check if session is expired
    if (new Date(sessionData.expiresAt) < new Date()) {
      return null
    }

    return sessionData as Session
  } catch (error) {
    console.error('Failed to parse session cookie:', error)
    return null
  }
}

export function isPublicRoute(pathname: string, publicRoutes: string[]): boolean {
  return publicRoutes.some(route => {
    if (route === '*') return true
    if (route.endsWith('*')) {
      return pathname.startsWith(route.slice(0, -1))
    }
    return pathname === route
  })
}

// Middleware configuration
export const authConfig = {
  matcher: [
    '/((?!api|_next/static|_next/image|favicon.ico).*)',
  ],
}
